import { CommonModule } from '@angular/common';
import { Component, EventEmitter, forwardRef, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { Question, Questionnaire } from '@app/core/models/questionnaire.model';
import { COUNTRY_CODES, CountryCode } from '@app/core/constants/country-codes.constants';

@Component({
  selector: 'app-mobile',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './mobile.html',
  styleUrl: './mobile.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => Mobile),
      multi: true
    }
  ]
})
export class Mobile implements OnInit {
  @Input() questionnaire: Questionnaire | null = null;
  @Input() question: Question | null = null;
  @Input() questionNumber: number = 0;
  @Input() isInvalid: boolean = false;
  @Input() errorMessage: string = '';
  @Input() formControl?: FormControl;
  @Input() placeholder: string = 'Enter Your Response';
  @Output() answerChange = new EventEmitter<any>();

  value: string = '';
  disabled: boolean = false;
  countryCode: string = '+91';
  countries: CountryCode[] = COUNTRY_CODES;

  // ControlValueAccessor callbacks
  onChange = (value: string) => {};
  onTouched = () => {};

  ngOnInit() {
    if (this.formControl) {
      const initial = this.formControl.value;
      if (initial && String(initial).trim()) {
        this.parseValue(String(initial).trim());
      } else {
        this.value = '';
      }
      this.formControl.valueChanges.subscribe(value => {
        if (value && String(value).trim()) {
          this.parseValue(String(value).trim());
        } else {
          this.value = '';
        }
      });
    }
  }

  writeValue(value: string): void {
    if (value) {
      this.parseValue(value);
    } else {
      this.value = '';
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  parseValue(value: string) {
    // Find the country code that matches start of value
    const matched = this.countries.find((c) => value.startsWith(c.dialCode));
    if (matched) {
      this.countryCode = matched.dialCode;
      this.value = value.slice(matched.dialCode.length).replace(/\D/g, '');
    } else {
      this.value = value.replace(/\D/g, '');
    }
  }

  onInputChange(event: any) {
    const input = event.target.value;
    this.value = input.replace(/\D/g, ''); // Only digits
    this.emitValue();
  }

  onCountryChange(event: any) {
    this.countryCode = event.target.value;
    this.emitValue();
  }

  emitValue() {
    // When number part is empty, treat as empty so required validation runs when user clears the field
    const isEmpty = this.value.trim() === '';
    const fullNumber = isEmpty ? '' : this.countryCode + this.value;
    this.onChange(fullNumber);
    this.onTouched();

    if (this.question?.questionId) {
      this.answerChange.emit({
        questionId: this.question.questionId,
        textResponse: fullNumber
      });
    }

    if (this.formControl) {
      this.formControl.setValue(fullNumber, { emitEvent: true });
      this.formControl.markAsTouched();
    }
  }

  allowOnlyNumbers(event: KeyboardEvent) {
    const key = event.key;
    if (!/[\d]/.test(key)) {
      event.preventDefault();
    }
  }

  allowOnlyPasteNumbers(event: ClipboardEvent) {
    const pasteData = event.clipboardData?.getData('text') || '';
    if (!/^\d*$/.test(pasteData)) {
      event.preventDefault();
    }
  }

  onBlur() {
    this.onTouched();
    if (this.formControl) {
      this.formControl.markAsTouched();
    }
  }
}
